import React, {ReactElement, useContext, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {Box} from '@vio/atlas/helpers/Box'
import {trackUserAuthClick} from '@vio/core/src/auth/modules/actions'
import {
  getIsAuthenticated,
  getSusiActive,
  selectAuthenticationError
} from '@vio/core/src/auth/modules/selectors'
import {
  AuthenticationFlows,
  AuthenticationMethod,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

import {setEmailSignUpVisibility} from '../../../Email/slice'
import {getHasSusiInUrl} from '../../../hooks/useSUSIBottomSheet'
import {BottomSheet} from './BottomSheet'
import {Agreement, GemsIcon, SignInButtons, SignInTitle} from './components'
import {SusiContext} from './SusiProvider'

interface Props {
  infoContext: Record<string, unknown>
  IconSlot: ReactElement | null
  TitleSlot: ReactElement | null
  MainContentSlot: ReactElement | null
  BottomContentSlot: ReactElement | null
  flow: AuthenticationFlows | null
}

export const SusiBottomSheet = ({
  IconSlot,
  TitleSlot,
  MainContentSlot,
  BottomContentSlot,
  flow
}: Props) => {
  const dispatch = useDispatch()
  const susiActive = useSelector(getSusiActive)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isOpen = susiActive && !isAuthenticated
  const {errorMessage, errorType} = useSelector(selectAuthenticationError)
  const hasSusiInUrl = getHasSusiInUrl()
  const {
    handleCloseOverlay,
    handleLogin,
    updateSUSILocationState,
    trackSignInError
  } = useContext(SusiContext)
  const toggle = useToggle()

  const handleOnClose = () => {
    if (hasSusiInUrl) {
      updateSUSILocationState({active: false})
    }
    handleCloseOverlay()
  }

  const handleEmailLogin = (e: React.SyntheticEvent) => {
    e.preventDefault()
    dispatch(
      trackUserAuthClick({
        authenticationMethod: AuthenticationMethod.Email,
        sourceComponent: SourceComponentType.Login
      })
    )
    dispatch(
      setEmailSignUpVisibility({
        isOpen: true,
        sourceComponent: SourceComponentType.Login
      })
    )
    handleOnClose()
  }

  const IconComponent = IconSlot ?? <GemsIcon />

  const SignInTitleComponent = TitleSlot ?? <SignInTitle />
  const SignInMainContentComponent = MainContentSlot ?? null
  const SignInBottomContentComponent = BottomContentSlot ?? (
    <>
      <SignInButtons
        handleLogin={handleLogin}
        handleEmailLogin={handleEmailLogin}
        handleOnClose={handleOnClose}
      />
      <Agreement />
    </>
  )

  const SignInStep = {
    TitleComponent: <div>{SignInTitleComponent}</div>,
    MainContentComponent: SignInMainContentComponent ? (
      <Box paddingBottom="s600">{SignInMainContentComponent}</Box>
    ) : null,
    BottomContentComponent: <div>{SignInBottomContentComponent}</div>
  }

  const {TitleComponent, MainContentComponent, BottomContentComponent} =
    SignInStep

  useEffect(() => {
    if (errorMessage && errorType) {
      trackSignInError({
        errorMessage,
        errorType
      })
    }
  }, [errorMessage, errorType, trackSignInError])

  // Check if we should hide the icon for PreCheckout flow on B side
  const shouldHideIcon =
    flow === AuthenticationFlows.DealFreeze ||
    (flow === AuthenticationFlows.PreCheckout &&
      toggle('567c4139-signin-to-complete-booking', false, true))

  return (
    <>
      <BottomSheet
        isOpen={isOpen}
        headerVariant={shouldHideIcon ? 'floating' : 'default'}
        IconComponent={shouldHideIcon ? undefined : IconComponent}
        TitleComponent={TitleComponent}
        MainContentComponent={MainContentComponent}
        BottomContentComponent={BottomContentComponent}
        onClose={handleOnClose}
      />
    </>
  )
}
